import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  RadioGroup,
  Radio,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  Button,
  Tooltip,
} from '@material-ui/core';
import { SwapHoriz } from '@material-ui/icons';
import Loading from 'components/Loading';
import { TRANSFER_TITLE_SITE_DOMAIN } from 'queries/titleContent';
import { Toast } from 'components/Toast';

import { UserService } from 'services/User/UserService';
import { useMutation } from '@apollo/client';

interface ITransferDomainProps {
  selectedTitles: Map<string, ITitle>;
  refetch: any;
}

const TransferDomain: React.FC<ITransferDomainProps> = ({
  selectedTitles,
  refetch,
}) => {
  const sites = UserService.useSitesFromAccess();

  const [open, setOpen] = useState(false);
  const [selectedSiteDomain, setSelectedSiteDomain] = useState('');
  const [openLoading, setOpenLoading] = useState(false);
  const radioGroupRef = useRef<HTMLElement>();

  const handleEntering = () => {
    if (radioGroupRef.current) {
      radioGroupRef.current.focus();
    }
  };

  const handleClose = () => {
    setOpen(!open);
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedSiteDomain(event.target.value);
  };

  const [transferSiteDomain] = useMutation(TRANSFER_TITLE_SITE_DOMAIN);

  const transferSingleTitle = async (title: ITitle) => {
    try {
      await transferSiteDomain({
        variables: {
          titleId: title.id,
          siteDomainDestination: selectedSiteDomain,
        },
      });
      return { success: true, title: title.title || title.id, error: null };
    } catch (error) {
      return {
        success: false,
        title: title.title || title.id,
        error:
          error instanceof Error ? error.message : 'Unknown error occurred',
      };
    }
  };

  const processTransferResults = (results: PromiseSettledResult<any>[]) => {
    const successfulTitles: string[] = [];
    const failedTitles: { title: string; error: string | null }[] = [];

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        if (result.value.success) {
          successfulTitles.push(result.value.title);
        } else {
          const errorMessage = result.value.error?.includes(
            'E11000 duplicate key error collection:',
          )
            ? 'Title already exists in the target domain.'
            : result.value.error;

          failedTitles.push({
            title: result.value.title,
            error: errorMessage,
          });
        }
      } else {
        failedTitles.push({
          title: 'Unknown title',
          error: result.reason?.message || 'Promise rejected',
        });
      }
    });

    return { successfulTitles, failedTitles };
  };

  const buildToastMessage = (
    successfulTitles: string[],
    failedTitles: { title: string; error: string | null }[],
  ) => {
    let message = '';
    let toastType: 'success' | 'error' | 'warning' = 'success';

    if (successfulTitles.length > 0 && failedTitles.length === 0) {
      message = `Successfully transferred ${successfulTitles.length} title(s) to ${selectedSiteDomain}`;
      toastType = 'success';
    } else if (successfulTitles.length === 0 && failedTitles.length > 0) {
      const errorMessages = failedTitles
        .map((failed) => `${failed.title}: ${failed.error}`)
        .join('\n');
      message = `Failed to transfer ${failedTitles.length} title(s):\n${errorMessages}`;
      toastType = 'error';
    } else if (successfulTitles.length > 0 && failedTitles.length > 0) {
      const errorMessages = failedTitles
        .map((failed) => `${failed.title}: ${failed.error}`)
        .join('\n');
      message = `Transfer completed: ${successfulTitles.length} successful, ${failedTitles.length} failed.\nErrors:\n${errorMessages}`;
      toastType = 'warning';
    }

    return { message, toastType };
  };

  const handleConfirm = async () => {
    setOpen(false);
    if (!selectedSiteDomain) return;

    setOpenLoading(true);

    try {
      const titleArray = Array.from(selectedTitles.values());
      const transferPromises = titleArray.map(transferSingleTitle);
      const results = await Promise.allSettled(transferPromises);

      const { successfulTitles, failedTitles } = processTransferResults(
        results,
      );
      const { message, toastType } = buildToastMessage(
        successfulTitles,
        failedTitles,
      );

      Toast(message, toastType);

      if (successfulTitles.length > 0) {
        setTimeout(() => {
          refetch();
        }, 2000);
      }
    } catch (error) {
      Toast('An unexpected error occurred during transfer.', 'error');
    } finally {
      setOpenLoading(false);
    }
  };

  return (
    <>
      <Tooltip title="Transfer to domain">
        <IconButton
          color="primary"
          aria-label="transfer domain"
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
        >
          <SwapHoriz />
        </IconButton>
      </Tooltip>

      <Loading openLoading={openLoading} />
      <Dialog
        disableBackdropClick
        disableEscapeKeyDown
        maxWidth="xs"
        onEntering={handleEntering}
        aria-labelledby="transfer-domain-title"
        onClose={handleClose}
        open={open}
      >
        <DialogTitle id="transfer-domain-title">Transfer to domain</DialogTitle>
        <DialogContent dividers>
          <RadioGroup
            ref={radioGroupRef}
            aria-label="site-domain"
            name="site-domain"
            value={selectedSiteDomain}
            onChange={handleChange}
          >
            {sites && sites.length > 0 ? (
              sites.map((site) => (
                <FormControlLabel
                  key={site}
                  value={site}
                  control={<Radio />}
                  label={site}
                />
              ))
            ) : (
              <FormControlLabel
                value=""
                control={<Radio />}
                label="No sites available"
                disabled
              />
            )}
          </RadioGroup>
        </DialogContent>
        <DialogActions>
          <Button autoFocus onClick={handleClose} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            variant="contained"
            disabled={!selectedSiteDomain}
          >
            Transfer
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TransferDomain;
